/**
 * Waiting for specific ROWS to finish — the "and wait" half of every dispatch helper whose caller is a human.
 *
 * WHY ROWS AND NOT "IS THIS STEP DONE?". Completion of a STEP is exam-wide — no live job of its types — and a
 * caller holding a chat turn open did not ask about the exam, it asked about its own dispatch. Waiting on the
 * exam-wide predicate would block on work someone else queued meanwhile (a refine landing during an extraction,
 * a re-grade during a re-grade) and report ITS failure as this caller's. The rows a dispatch created are exactly
 * the caller's work, so their terminal status is the outcome it may report. Every dispatch helper waits this way
 * now, grading included — `waitForGradingBatch` was the last exception and it is gone.
 *
 * THE THREE OUTCOMES ARE NOT TWO. A tool that only distinguished success from failure had to call a timeout a
 * failure, which is the one thing it is not: the work is still queued, still leased, still going to run. Saying
 * "failed" there tells the teacher to retry something that is already in flight. So `pending` is its own answer
 * and every caller words it as "still running, you will be told".
 */

import { logger } from '@adornis/base/logging.js';
import { getJob } from './job-store.js';

/** How often a waiting caller re-asks. Two seconds is the runner's own tick — asking faster learns nothing. */
const POLL_MS = 2000;

export type JobWaitOutcome =
  /** Every row reached `done` (or was pruned, which means it finished long ago). */
  | 'done'
  /** At least one row ended `failed` or `cancelled`. The caller must not report success. */
  | 'failed'
  /** Still running when the caller ran out of patience. NOT a failure — see the header. */
  | 'pending';

/**
 * Polls `jobIds` until all are done, one has failed, or `timeoutMs` elapses.
 *
 * FAILS FAST on the first failed row rather than waiting for the rest: the caller's report is already decided
 * at that point, and every extra poll is a teacher watching a spinner for an answer that will not change.
 *
 * A row that is GONE counts as done. Finished rows survive `PRUNE_AFTER_MS` (a week), so a row that vanished
 * inside a caller's wait finished long before the wait began — treating it as unfinished would hang forever.
 *
 * `context` is merged into the log lines; pass whatever identifies the work (`{ examId, artifact }`).
 *
 */
export async function waitForJobRows(
  jobIds: string[],
  context: Record<string, unknown>,
  timeoutMs: number,
): Promise<JobWaitOutcome> {
  if (jobIds.length === 0) return 'done';

  const deadline = Date.now() + timeoutMs;
  for (;;) {
    const jobs = await Promise.all(jobIds.map(jobId => getJob(jobId)));

    // SUPERSEDED IS NOT FAILED, and it is not done either. A row cancelled with this gate lost a dedupe race on its
    // way back to `pending` — an equivalent row is live and is doing the work (see `supersede` in the store, and
    // `dedupeUntil`). Reporting failure would tell the teacher to retry work that is running; `pending` is exactly
    // what this caller should say, and it is what every caller already words as "still running".
    if (jobs.some(job => job?.status === 'cancelled' && job.lastGate === 'superseded')) {
      logger.info({ ...context }, 'A waited-on row was superseded by an equivalent one — reporting it as pending');
      return 'pending';
    }

    const failure = jobs.find(job => job?.status === 'failed' || job?.status === 'cancelled');
    if (failure) {
      logger.warn(
        { ...context, jobId: failure._id, status: failure.status, error: failure.lastError },
        'Job did not succeed',
      );
      return 'failed';
    }

    if (jobs.every(job => !job || job.status === 'done')) return 'done';

    if (Date.now() >= deadline) {
      const outstanding = jobs.filter(job => job && job.status !== 'done').map(job => job?._id);
      logger.info({ ...context, outstanding }, "Job outlived its caller's wait");
      return 'pending';
    }

    await new Promise<void>(resolve => {
      setTimeout(resolve, POLL_MS);
    });
  }
}
